import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { eur, todayISO } from "@/lib/format";

const TIPI = ["RDO", "ODA", "CEL"];
const EMPTY = {
  tipo: "RDO", cantiere_id: "", data: todayISO(), fornitore_id: "",
  telefono: "", email: "", indirizzo: "", citta: "", cap: "", provincia: "", partita_iva: "",
  condizioni_pagamento: "", porto: "", codice: "", tipo_fornitura: "", rif_offerta: "", note: "",
  direttore_cantiere: "Luigi Vallefuoco", direttore_lavori: "Luigi Vallefuoco",
  oggetto_appalto: "", subappaltatore: "", contratto_rif: "", data_ultimazione: "", regola_arte: true, note_lavorazione: "",
  articoli: [],
};
const newArt = () => ({ descrizione: "", marca: "", modello: "", cod_art: "", quantita: "1", unita: "", prezzo_unitario: "0" });

export default function DocumentoModal({ open, onOpenChange, cantieri, fornitori, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [numero, setNumero] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ...EMPTY, ...initial,
        cantiere_id: initial.cantiere_id || "", fornitore_id: initial.fornitore_id || "",
        articoli: (initial.articoli || []).map((a) => ({
          descrizione: a.descrizione || "", marca: a.marca || "", modello: a.modello || "", cod_art: a.cod_art || "",
          quantita: String(a.quantita ?? "1"), unita: a.unita || "", prezzo_unitario: String(a.prezzo_unitario ?? "0"),
        })),
      });
      setNumero(initial.numero || "");
    } else {
      setForm(EMPTY);
      setNumero("");
    }
  }, [open, initial]);

  // Numero automatico per nuovi documenti, in base al tipo
  useEffect(() => {
    if (open && !isEdit && form.tipo) {
      api.get("/documenti/next-numero", { tipo: form.tipo }).then((r) => setNumero(r.numero)).catch(() => setNumero(""));
    }
  }, [open, isEdit, form.tipo]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCommessa = (id) => {
    const c = cantieri.find((x) => x.id === id);
    setForm((prev) => ({ ...prev, cantiere_id: id, codice: c?.codice_commessa || prev.codice, direttore_cantiere: c?.direttore_cantiere_nome || prev.direttore_cantiere }));
  };

  const onFornitore = (id) => {
    const f = fornitori.find((x) => x.id === id);
    setForm((prev) => ({
      ...prev, fornitore_id: id,
      telefono: f?.telefono || prev.telefono, email: f?.email || prev.email,
      partita_iva: f?.piva || prev.partita_iva,
      indirizzo: f?.indirizzo || prev.indirizzo, citta: f?.citta || prev.citta,
      cap: f?.cap || prev.cap, provincia: f?.provincia || prev.provincia,
    }));
  };

  const addArt = () => setForm((f) => ({ ...f, articoli: [...f.articoli, newArt()] }));
  const removeArt = (i) => setForm((f) => ({ ...f, articoli: f.articoli.filter((_, idx) => idx !== i) }));
  const setArt = (i, field, val) => setForm((f) => ({ ...f, articoli: f.articoli.map((r, idx) => idx === i ? { ...r, [field]: val } : r) }));

  const totale = form.articoli.reduce((s, a) => s + (Number(a.quantita) || 0) * (Number(a.prezzo_unitario) || 0), 0);

  const submit = async () => {
    if (!form.data) return toast.error("Seleziona la data.");
    const payload = {
      tipo: form.tipo,
      cantiere_id: form.cantiere_id || null,
      data: form.data,
      fornitore_id: form.fornitore_id || null,
      telefono: form.telefono, email: form.email, indirizzo: form.indirizzo,
      citta: form.citta, cap: form.cap, provincia: form.provincia, partita_iva: form.partita_iva,
      condizioni_pagamento: form.condizioni_pagamento, porto: form.porto, codice: form.codice,
      tipo_fornitura: form.tipo_fornitura, rif_offerta: form.rif_offerta, note: form.note,
      direttore_cantiere: form.direttore_cantiere, direttore_lavori: form.direttore_lavori,
      oggetto_appalto: form.oggetto_appalto, subappaltatore: form.subappaltatore, contratto_rif: form.contratto_rif,
      data_ultimazione: form.data_ultimazione, regola_arte: form.regola_arte, note_lavorazione: form.note_lavorazione,
      articoli: form.articoli.filter((a) => a.descrizione.trim()).map((a) => ({
        descrizione: a.descrizione.trim(), marca: a.marca || "", modello: a.modello || "", cod_art: a.cod_art || "",
        quantita: Number(a.quantita) || 0, unita: a.unita || "", prezzo_unitario: Number(a.prezzo_unitario) || 0,
      })),
    };
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/documenti/${initial.id}`, payload);
        toast.success("Documento aggiornato.");
      } else {
        await api.post("/documenti", payload);
        toast.success("Documento salvato con successo.");
      }
      onOpenChange(false); onSaved?.();
    } catch (err) {
      toast.error(err.message || "Salvataggio non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="documento-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifica ${form.tipo}` : "Nuovo Documento (RDO / ODA / CEL)"}</DialogTitle>
          <DialogDescription>Compila la richiesta d'offerta, l'ordine d'acquisto o il certificato.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Tipo Documento</Label>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="doc-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Commessa</Label>
            <Select value={form.cantiere_id} onValueChange={onCommessa}>
              <SelectTrigger data-testid="doc-commessa-select"><SelectValue placeholder="Seleziona commessa..." /></SelectTrigger>
              <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Numero (Automatico)</Label>
              <Input value={numero} readOnly data-testid="doc-numero-input" className="bg-secondary text-muted-foreground" />
            </div>
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" data-testid="doc-data-input" value={form.data} onChange={(e) => set("data", e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Fornitore</Label>
            <Select value={form.fornitore_id} onValueChange={onFornitore}>
              <SelectTrigger data-testid="doc-fornitore-select"><SelectValue placeholder="Seleziona fornitore..." /></SelectTrigger>
              <SelectContent>{fornitori.map((f) => <SelectItem key={f.id} value={f.id}>{f.ragione_sociale}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="doc-telefono-input" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="doc-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          </div>

          <div className="space-y-1.5"><Label>Indirizzo</Label><Input data-testid="doc-indirizzo-input" value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} /></div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Città</Label><Input data-testid="doc-citta-input" value={form.citta} onChange={(e) => set("citta", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>CAP</Label><Input data-testid="doc-cap-input" value={form.cap} onChange={(e) => set("cap", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid="doc-provincia-input" value={form.provincia} onChange={(e) => set("provincia", e.target.value)} /></div>
          </div>

          <div className="space-y-1.5"><Label>Partita IVA</Label><Input data-testid="doc-piva-input" value={form.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} /></div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Condizioni Pagamento</Label><Input data-testid="doc-pagamento-input" value={form.condizioni_pagamento} onChange={(e) => set("condizioni_pagamento", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Rif. Offerta</Label><Input data-testid="doc-rifofferta-input" value={form.rif_offerta} onChange={(e) => set("rif_offerta", e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Codice</Label><Input data-testid="doc-codice-input" value={form.codice} onChange={(e) => set("codice", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tipo Fornitura</Label><Input data-testid="doc-tipofornitura-input" value={form.tipo_fornitura} onChange={(e) => set("tipo_fornitura", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Porto</Label><Input data-testid="doc-porto-input" value={form.porto} onChange={(e) => set("porto", e.target.value)} /></div>
          </div>

          {form.tipo === "CEL" && (
            <div className="space-y-4 rounded-lg border border-border p-3 bg-secondary/30" data-testid="doc-cel-block">
              <p className="text-sm font-semibold">Dati Certificato (CEL)</p>
              <div className="space-y-1.5"><Label>Oggetto dell'appalto affidato</Label><Textarea rows={2} data-testid="doc-oggetto-input" value={form.oggetto_appalto} onChange={(e) => set("oggetto_appalto", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Subappaltatore</Label><Input data-testid="doc-subappaltatore-input" value={form.subappaltatore} onChange={(e) => set("subappaltatore", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Contratto / Rif. Offerta</Label><Input data-testid="doc-contratto-input" value={form.contratto_rif} onChange={(e) => set("contratto_rif", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Data Ultimazione Lavori</Label><Input type="date" data-testid="doc-dataultim-input" value={form.data_ultimazione} onChange={(e) => set("data_ultimazione", e.target.value)} /></div>
                <div className="space-y-1.5">
                  <Label>Eseguito a regola d'arte</Label>
                  <Select value={form.regola_arte ? "si" : "no"} onValueChange={(v) => set("regola_arte", v === "si")}>
                    <SelectTrigger data-testid="doc-regolaarte-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="si">Sì</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Note sulla lavorazione</Label><Textarea rows={2} data-testid="doc-notelavorazione-input" value={form.note_lavorazione} onChange={(e) => set("note_lavorazione", e.target.value)} /></div>
            </div>
          )}

          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} data-testid="doc-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} /></div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Direttore Cantiere</Label><Input data-testid="doc-dircantiere-input" value={form.direttore_cantiere} onChange={(e) => set("direttore_cantiere", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Direttore Lavori</Label><Input data-testid="doc-dirlavori-input" value={form.direttore_lavori} onChange={(e) => set("direttore_lavori", e.target.value)} /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Articoli</Label>
              <Button type="button" variant="outline" size="sm" className="h-8 gap-1" data-testid="doc-add-articolo" onClick={addArt}>
                <Plus className="h-4 w-4" /> Aggiungi Articolo
              </Button>
            </div>
            {form.articoli.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nessun articolo aggiunto.</p>
            ) : (
              <div className="space-y-2">
                {form.articoli.map((a, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-center border-b border-border/50 pb-2" data-testid={`doc-articolo-${i}`}>
                    <Input className="flex-1 min-w-[160px]" placeholder="Descrizione" data-testid={`doc-art-desc-${i}`} value={a.descrizione} onChange={(e) => setArt(i, "descrizione", e.target.value)} />
                    {form.tipo !== "CEL" && <>
                      <Input className="w-24" placeholder="Marca" data-testid={`doc-art-marca-${i}`} value={a.marca} onChange={(e) => setArt(i, "marca", e.target.value)} />
                      <Input className="w-24" placeholder="Modello" data-testid={`doc-art-modello-${i}`} value={a.modello} onChange={(e) => setArt(i, "modello", e.target.value)} />
                      <Input className="w-24" placeholder="Cod Art" data-testid={`doc-art-codart-${i}`} value={a.cod_art} onChange={(e) => setArt(i, "cod_art", e.target.value)} />
                    </>}
                    <Input type="number" step="0.01" min="0" className="w-20" placeholder="Q.tà" data-testid={`doc-art-qta-${i}`} value={a.quantita} onChange={(e) => setArt(i, "quantita", e.target.value)} />
                    <Input className="w-20" placeholder="U.M." data-testid={`doc-art-unita-${i}`} value={a.unita} onChange={(e) => setArt(i, "unita", e.target.value)} />
                    {form.tipo !== "RDO" && <>
                      <Input type="number" step="0.01" min="0" className="w-28" placeholder="Prezzo €" data-testid={`doc-art-prezzo-${i}`} value={a.prezzo_unitario} onChange={(e) => setArt(i, "prezzo_unitario", e.target.value)} />
                      <span className="w-24 text-right font-mono text-sm text-muted-foreground">{eur((Number(a.quantita) || 0) * (Number(a.prezzo_unitario) || 0))}</span>
                    </>}
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => removeArt(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {form.tipo !== "RDO" && <div className="flex justify-end pt-1 text-sm font-semibold">Totale: <span className="ml-2 font-mono" data-testid="doc-totale">{eur(totale)}</span></div>}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-cancel-button">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="doc-save-button"
            className="bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva Documento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
